/**
 * Benefit Definitions Loader
 *
 * Provides a unified interface to load benefit definitions for any coverage type.
 * Supports both:
 * - New LOC_MAP pattern (migrated LOCs like Cancer)
 * - Legacy pattern (benefits/definitions files for non-migrated LOCs)
 *
 * Usage:
 * ```typescript
 * const definitions = await getBenefitDefinitions('cancer');
 * const cachedDefs = getBenefitDefinitionsSync('dental');
 * ```
 */

import { logger } from "@platform/browser-logger";

import { LOC_MAP, type LocCoverageType } from "../locs/locMap";

import type { BenefitDefinitions } from "./benefit.types";

const definitionsCache = new Map<string, BenefitDefinitions>();

export async function getBenefitDefinitions(
  coverageType: LocCoverageType,
): Promise<BenefitDefinitions> {
  if (definitionsCache.has(coverageType)) {
    logger.info(
      `[getBenefitDefinitions] Using cached definitions for ${coverageType}`,
    );
    return definitionsCache.get(coverageType)!;
  }

  const locConfig = LOC_MAP[coverageType];
  logger.info(
    `[getBenefitDefinitions] Loading definitions for ${coverageType}`,
  );
  definitionsCache.set(coverageType, locConfig.definitions);
  return locConfig.definitions;
}

/**
 * Get benefit definitions synchronously (requires pre-loaded cache)
 *
 * @param coverageType - The type of coverage
 * @returns BenefitDefinitions - The cached benefit definitions
 * @throws Error if definitions are not in cache
 */
export function getBenefitDefinitionsSync(
  coverageType: LocCoverageType,
): BenefitDefinitions {
  if (!definitionsCache.has(coverageType)) {
    throw new Error(
      `Benefit definitions for '${coverageType}' not found in cache. ` +
        `Use getBenefitDefinitions() first.`,
    );
  }

  return definitionsCache.get(coverageType)!;
}

/**
 * Preload benefit definitions for multiple coverage types
 *
 * @param coverageTypes - Array of coverage types to preload
 * @returns Promise<void>
 */
export async function preloadBenefitDefinitions(
  coverageTypes: LocCoverageType[],
): Promise<void> {
  const promises = coverageTypes.map((type) => getBenefitDefinitions(type));
  await Promise.all(promises);
}

/**
 * Check if definitions for a coverage type are cached
 *
 * @param coverageType - The coverage type to check
 * @returns boolean - True if cached, false otherwise
 */
export function isBenefitDefinitionsCached(
  coverageType: LocCoverageType,
): boolean {
  return definitionsCache.has(coverageType);
}

/**
 * Clear the cache for a specific coverage type or all types
 *
 * @param coverageType - Optional specific coverage type to clear
 */
export function clearBenefitDefinitionsCache(
  coverageType?: LocCoverageType,
): void {
  if (coverageType) {
    definitionsCache.delete(coverageType);
  } else {
    definitionsCache.clear();
  }
}

/**
 * Get all cached coverage types
 *
 * @returns string[] - Array of cached coverage type names
 */
export function getCachedBenefitDefinitionTypes(): string[] {
  return Array.from(definitionsCache.keys());
}
